// @ts-nocheck

import React, { useState, useEffect, useMemo } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Switch,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import AtomButton from '@/components/atoms/Button';
import AtomSlider from '@/components/atoms/Slider';
import AtomTextField from '@/components/atoms/TextField';
import segmentStyles from '@/pages/Segments/segments.module.css';
import { AppDispatch } from '@/store';
import { getStudyName, getYearSelected } from '@/store/studies/selectors';
import {
  selectCurrentStudyYearCategoryCompany,
  selectStudyYearCategoryCompaniesLoading,
  selectStudyYearCategoryCompaniesUpdating,
  selectStudyYearCategoryCompaniesError,
  selectParentCompany,
} from '@/store/study-year-category-companies/selectors';
import { studyYearCategoryCompanies } from '@/store/study-year-category-companies/thunks';

interface ConcentrationItem {
  id: string;
  category_name: string;
  revenue: number;
  concentration: number;
  concentration_units_sold: number;
  units_sold: number;
  selling_price: number;
  note: string;
  status: string;
}

interface ConcentrationDialogContentProps {
  selectedRow: {
    study_year_category_company_id: string;
    company_name: string;
    [key: string]: any;
  };
  selectedSegment: string;
  onClose: () => void;
  onSuccess: () => void;
}

type DialogView = 'main' | 'note' | 'versions' | 'status';

const ConcentrationDialogContent: React.FC<ConcentrationDialogContentProps> = ({
  selectedRow,
  selectedSegment,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  // Selectors
  const currentUser = useSelector((state: any) => state.auth?.user);
  const useStudyName = useSelector(getStudyName);
  const useYearSelected = useSelector(getYearSelected);
  const currentStudyYearCategoryCompany = useSelector(selectCurrentStudyYearCategoryCompany);
  const parentCompany = useSelector(selectParentCompany);
  const isLoading = useSelector(selectStudyYearCategoryCompaniesLoading);
  const isUpdating = useSelector(selectStudyYearCategoryCompaniesUpdating);
  const error = useSelector(selectStudyYearCategoryCompaniesError);

  // Local state
  const [concentrations, setConcentrations] = useState<ConcentrationItem[]>([]);
  const [localError, setLocalError] = useState('');
  const [dialogView, setDialogView] = useState<DialogView>('main');
  const [selectedConcentrationIndex, setSelectedConcentrationIndex] = useState<number | null>(null);
  const [noteText, setNoteText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [concentrationVersions, setConcentrationVersions] = useState<any[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'revenue' | 'units'>('revenue');

  const statusOptions = [
    { value: 'estimated', label: 'Estimated' },
    { value: 'modelled', label: 'Modelled' },
    { value: 'revised', label: 'Revised' },
    { value: 'override', label: 'Override' },
    { value: 'supplier_input', label: 'Supplier Input' },
  ];

  const totalRevenue = useMemo(() => {
    return parentCompany?.revenue || 0;
  }, [parentCompany?.revenue]);

  const currentChildrenSum = useMemo(
    () => concentrations.reduce((sum, item) => sum + item.revenue, 0),
    [concentrations]
  );

  const unallocatedRevenue = useMemo(
    () => totalRevenue - currentChildrenSum,
    [totalRevenue, currentChildrenSum]
  );

  const totalUnits = useMemo(() => parentCompany?.units_sold || 0, [parentCompany]);

  // Fetch data when component mounts
  useEffect(() => {
    if (selectedRow?.study_year_category_company_id) {
      dispatch(
        studyYearCategoryCompanies.getStudyYearCategoryCompanyParent({
          studyYearCategoryCompanyId: selectedRow.study_year_category_company_id,
        })
      );
    }
  }, [selectedRow?.study_year_category_company_id, dispatch]);

  // Update concentrations when data is loaded
  useEffect(() => {
    if (parentCompany?.children && Array.isArray(parentCompany.children)) {
      setConcentrations(
        parentCompany.children.map((item: any) => ({
          id: item.id,
          category_name: item.category_name,
          revenue: Number(item.revenue),
          concentration: item.concentration,
          concentration_units_sold: item.concentration_units_sold || 0,
          units_sold: item.units_sold || 0,
          selling_price: item.selling_price || 0,
          note: item.note || '',
          status: item.status || 'estimated',
        }))
      );
    } else {
      // Handle case where no children data exists
      setConcentrations([]);
    }
  }, [parentCompany]);

  // Handle Redux errors
  useEffect(() => {
    if (error) {
      console.error('Study Year Category Company Error:', error);
    }
  }, [error]);

  const handleUnitsSliderChange = (index: number, _: Event, newValue: number | number[]) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    const newConcentrationUnits = value / 100;
    const newUnits = totalUnits * newConcentrationUnits;

    // Calculate total of other unit concentrations
    const totalOtherConcentrations = concentrations.reduce(
      (sum, item, i) => (i !== index ? sum + (item.concentration_units_sold || 0) : sum),
      0
    );

    if (totalOtherConcentrations + newConcentrationUnits > 1) {
      setLocalError('Total units concentration cannot exceed 100%');
      return;
    }

    const newConcentrations = [...concentrations];
    newConcentrations[index] = {
      ...newConcentrations[index],
      units_sold: Number(newUnits.toFixed(0)),
      concentration_units_sold: newConcentrationUnits,
    };

    setLocalError('');
    setConcentrations(newConcentrations);
  };

  const handleUnitsChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    const newUnits = Number(newValue);

    if (isNaN(newUnits)) return;

    const newConcentrationUnits = newUnits / totalUnits;

    // Calculate total of other units
    const totalOtherUnits = concentrations.reduce(
      (sum, item, i) => (i !== index ? sum + item.units_sold : sum),
      0
    );

    if (totalOtherUnits + newUnits > totalUnits) {
      setLocalError(`Total units cannot exceed ${totalUnits}`);
      return;
    }

    const newConcentrations = [...concentrations];
    newConcentrations[index] = {
      ...newConcentrations[index],
      units_sold: newUnits,
      concentration_units_sold: newConcentrationUnits,
    };

    setLocalError('');
    setConcentrations(newConcentrations);
  };

  // Dialog view functions
  const getDialogTitle = () => {
    if (dialogView === 'main') return selectedRow?.company_name || 'Company Segment';

    const categoryName =
      selectedConcentrationIndex !== null
        ? concentrations[selectedConcentrationIndex]?.category_name
        : 'Category';

    switch (dialogView) {
      case 'note':
        return `${categoryName} - Add Note`;
      case 'versions':
        return `${categoryName} - Version History`;
      case 'status':
        return `${categoryName} - Change Status`;
      default:
        return selectedRow?.company_name || 'Company Segment';
    }
  };

  const getBreadcrumb = () => {
    if (dialogView === 'main') return null;

    const categoryName =
      selectedConcentrationIndex !== null
        ? concentrations[selectedConcentrationIndex]?.category_name
        : 'Category';

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
          p: 1,
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <AtomButton
          variant="text"
          size="small"
          onClick={() => setDialogView('main')}
          style={{ marginRight: '16px' }}
        >
          <ArrowBackIcon style={{ marginRight: '8px' }} />
          Back to Concentrations
        </AtomButton>
        <Typography variant="body2" color="text.secondary">
          {selectedRow?.company_name} / {categoryName}
        </Typography>
      </Box>
    );
  };

  // Button handlers
  const handleNoteClick = (index: number) => {
    setSelectedConcentrationIndex(index);
    setNoteText(concentrations[index]?.note || '');
    setDialogView('note');
  };

  const handleVersionsClick = async (index: number) => {
    setSelectedConcentrationIndex(index);
    setDialogView('versions');
    setVersionsLoading(true);

    try {
      // Mock data for now - replace with your actual API call
      setTimeout(() => {
        setConcentrationVersions([
          {
            id: '1',
            time_created: new Date().toISOString(),
            revenue: concentrations[index].revenue * 0.9,
            concentration: concentrations[index].concentration * 0.9,
            status: 'modelled',
            note: 'Previous version',
          },
        ]);
        setVersionsLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Error fetching versions:', error);
      setVersionsLoading(false);
    }
  };

  const handleStatusClick = (index: number) => {
    setSelectedConcentrationIndex(index);
    setSelectedStatus(concentrations[index]?.status || 'estimated');
    setDialogView('status');
  };

  const handleNoteSave = () => {
    if (selectedConcentrationIndex !== null) {
      const newConcentrations = [...concentrations];
      newConcentrations[selectedConcentrationIndex] = {
        ...newConcentrations[selectedConcentrationIndex],
        note: noteText,
      };
      setConcentrations(newConcentrations);
    }
    setDialogView('main');
    setNoteText('');
    setSelectedConcentrationIndex(null);
  };

  const handleStatusSave = () => {
    if (selectedConcentrationIndex !== null) {
      const newConcentrations = [...concentrations];
      newConcentrations[selectedConcentrationIndex] = {
        ...newConcentrations[selectedConcentrationIndex],
        status: selectedStatus,
      };
      setConcentrations(newConcentrations);
    }
    setDialogView('main');
    setSelectedStatus('');
    setSelectedConcentrationIndex(null);
  };

  // Data handlers
  const handleSliderChange = (index: number, _: Event, newValue: number | number[]) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    const newConcentration = value / 100;
    const newRevenue = totalRevenue * newConcentration;

    const totalOtherConcentrations = concentrations.reduce(
      (sum, item, i) => (i !== index ? sum + item.concentration : sum),
      0
    );

    if (totalOtherConcentrations + newConcentration > 1) {
      setLocalError('Total concentration cannot exceed 100%');
      return;
    }

    const newConcentrations = [...concentrations];
    newConcentrations[index] = {
      ...newConcentrations[index],
      revenue: Number(newRevenue.toFixed(2)),
      concentration: newConcentration,
    };

    setLocalError('');
    setConcentrations(newConcentrations);
  };

  const handleRevenueChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    const newRevenue = Number(newValue);

    if (isNaN(newRevenue)) return;

    const totalOtherRevenue = concentrations.reduce(
      (sum, item, i) => (i !== index ? sum + item.revenue : sum),
      0
    );

    if (totalOtherRevenue + newRevenue > totalRevenue) {
      setLocalError(
        `Total revenue cannot exceed parent revenue of $${totalRevenue.toLocaleString()}`
      );
      return;
    }
    const newConcentration = newRevenue / totalRevenue;

    const newConcentrations = [...concentrations];
    newConcentrations[index] = {
      ...newConcentrations[index],
      revenue: newRevenue,
      concentration: newConcentration,
    };

    setLocalError('');
    setConcentrations(newConcentrations);
  };

  const submitEditConcentration = async () => {
    try {
      const currentTotal = concentrations.reduce((sum, item) => sum + item.revenue, 0);

      if (Math.abs(currentTotal - totalRevenue) > 0.01) {
        setLocalError(
          `Revenue allocation mismatch!\n` +
            `Parent Revenue: $${totalRevenue.toLocaleString()}\n` +
            `Allocated Total: $${currentTotal.toLocaleString()}\n` +
            `Difference: $${(totalRevenue - currentTotal).toLocaleString()}\n\n` +
            `Please adjust the concentrations so they total exactly $${totalRevenue.toLocaleString()}.`
        );
        return;
      }

      const totalConcentration = concentrations.reduce((sum, item) => sum + item.concentration, 0);
      if (Math.abs(totalConcentration - 1) > 0.001) {
        setLocalError(
          `Concentration mismatch! Total concentration is ${(totalConcentration * 100).toFixed(2)}% but should be 100%.`
        );
        return;
      }

      const updatePayload = {
        studyYearCategoryCompanyId: parentCompany?.id,
        id: parentCompany?.id,
        user_id: currentUser?.id,
        status: parentCompany?.status || 'active',
        analysis: parentCompany?.analysis || '',
        note: parentCompany?.note || '',
        children: concentrations.map(item => ({
          id: item.id,
          concentration: Number(item.concentration.toFixed(6)), // Ensure precision
          concentration_units_sold: Number((item.concentration_units_sold || 0).toFixed(6)),
          revenue: Number(item.revenue.toFixed(2)),
          units_sold: Math.round(item.units_sold || 0),
          selling_price: Number((item.selling_price || 0).toFixed(2)),
        })),
      };

      console.log('Submitting concentration update:', {
        parentRevenue: totalRevenue,
        allocatedTotal: currentTotal,
        concentrationTotal: totalConcentration,
        payload: updatePayload,
      });

      // Use the thunk to update
      await dispatch(
        studyYearCategoryCompanies.updateStudyYearCategoryCompany(updatePayload)
      ).unwrap();

      // Success! Clear errors and close dialog
      setLocalError('');
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error updating concentrations:', err);

      // Handle different types of errors
      if (err?.message) {
        setLocalError(`Update failed: ${err.message}`);
      } else if (typeof err === 'string') {
        setLocalError(`Update failed: ${err}`);
      } else {
        setLocalError('Failed to update concentrations. Please check your data and try again.');
      }
    }
  };

  // Content renderers
  const renderNoteContent = () => (
    <Box sx={{ py: 2 }}>
      <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Add Note for {concentrations[selectedConcentrationIndex]?.category_name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Current Revenue:{' '}
          {new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(concentrations[selectedConcentrationIndex]?.revenue || 0)}
        </Typography>
      </Box>

      <AtomTextField
        label="Note"
        multiline
        rows={4}
        fullWidth
        value={noteText}
        onChange={e => setNoteText(e.target.value)}
        placeholder="Enter your note here..."
        variant="outlined"
      />

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <AtomButton variant="outlined" onClick={() => setDialogView('main')}>
          Cancel
        </AtomButton>
        <AtomButton variant="contained" onClick={handleNoteSave}>
          Save Note
        </AtomButton>
      </Box>
    </Box>
  );

  const renderStatusContent = () => (
    <Box sx={{ py: 2 }}>
      <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Change Status for {concentrations[selectedConcentrationIndex]?.category_name}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Current Status:
        </Typography>
        <Chip
          label={concentrations[selectedConcentrationIndex]?.status || 'estimated'}
          size="small"
          color={
            concentrations[selectedConcentrationIndex]?.status === 'override'
              ? 'success'
              : 'default'
          }
        />
      </Box>

      <FormControl fullWidth>
        <InputLabel>Status</InputLabel>
        <Select
          value={selectedStatus}
          onChange={e => setSelectedStatus(e.target.value)}
          label="Status"
        >
          {statusOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              <Chip
                label={option.label}
                size="small"
                color={option.value === 'override' ? 'success' : 'default'}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <AtomButton variant="outlined" onClick={() => setDialogView('main')}>
          Cancel
        </AtomButton>
        <AtomButton variant="contained" onClick={handleStatusSave}>
          Update Status
        </AtomButton>
      </Box>
    </Box>
  );

  const renderVersionsContent = () => (
    <Box sx={{ py: 2 }}>
      <Typography variant="h6" gutterBottom>
        Version History for {concentrations[selectedConcentrationIndex]?.category_name}
      </Typography>

      {versionsLoading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={40} />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading versions...
          </Typography>
        </Box>
      ) : (
        <Box>
          {concentrationVersions.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
              No previous versions found.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Revenue</TableCell>
                    <TableCell>Concentration</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Note</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {concentrationVersions.map(version => (
                    <TableRow key={version.id}>
                      <TableCell>{new Date(version.time_created).toLocaleString()}</TableCell>
                      <TableCell>
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }).format(version.revenue)}
                      </TableCell>
                      <TableCell>{((version.concentration || 0) * 100).toFixed(2)}%</TableCell>
                      <TableCell>
                        <Chip label={version.status} size="small" />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>{version.note || '-'}</TableCell>
                      <TableCell>
                        <AtomButton
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            alert(`Restored version ${version.id}`);
                            setDialogView('main');
                          }}
                        >
                          Restore
                        </AtomButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <AtomButton variant="outlined" onClick={() => setDialogView('main')}>
          Back to Concentrations
        </AtomButton>
      </Box>
    </Box>
  );

  const renderRevenueStatusUI = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
        p: 2,
        bgcolor: Math.abs(unallocatedRevenue) > 0.01 ? '#fff3e0' : '#f8f9fa',
        borderRadius: 1,
        border: Math.abs(unallocatedRevenue) > 0.01 ? '1px solid #ff9800' : '1px solid #e3f2fd',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="h6">
          {viewMode === 'revenue' ? 'Revenue Distribution' : 'Units Distribution'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Typography variant="body2">
            <strong>Parent Revenue (Fixed):</strong> ${totalRevenue.toLocaleString()}
          </Typography>

          <Typography
            variant="body2"
            color={Math.abs(currentChildrenSum - totalRevenue) > 0.01 ? 'error' : 'success.main'}
          >
            <strong>Allocated:</strong> ${currentChildrenSum.toLocaleString()}
          </Typography>

          <Typography
            variant="body2"
            color={Math.abs(unallocatedRevenue) > 0.01 ? 'warning.main' : 'success.main'}
            sx={{ fontWeight: Math.abs(unallocatedRevenue) > 0.01 ? 'bold' : 'normal' }}
          >
            <strong>Remaining:</strong> ${unallocatedRevenue.toLocaleString()}
          </Typography>

          <Typography variant="body2">
            <strong>Total Units:</strong> {totalUnits.toLocaleString()}
          </Typography>
        </Box>

        {/* Progress bar showing allocation status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Typography variant="caption" sx={{ minWidth: '60px' }}>
            Allocation:
          </Typography>
          <Box
            sx={{
              width: 200,
              height: 8,
              bgcolor: '#f0f0f0',
              borderRadius: 1,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: `${Math.min((currentChildrenSum / totalRevenue) * 100, 100)}%`,
                height: '100%',
                bgcolor:
                  Math.abs(currentChildrenSum - totalRevenue) > 0.01
                    ? currentChildrenSum > totalRevenue
                      ? '#f44336'
                      : '#ff9800'
                    : '#4caf50',
                transition: 'all 0.3s ease',
              }}
            />
          </Box>
          <Typography
            variant="caption"
            color={Math.abs(currentChildrenSum - totalRevenue) > 0.01 ? 'error' : 'success.main'}
            sx={{ minWidth: '45px' }}
          >
            {((currentChildrenSum / totalRevenue) * 100).toFixed(1)}%
          </Typography>
        </Box>

        {/* Warning message for allocation issues */}
        {Math.abs(unallocatedRevenue) > 0.01 && (
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="caption"
              color="warning.main"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontWeight: 'bold',
              }}
            >
              ⚠️{' '}
              {unallocatedRevenue > 0
                ? `$${unallocatedRevenue.toLocaleString()} needs to be allocated`
                : `Over-allocated by $${Math.abs(unallocatedRevenue).toLocaleString()}`}
            </Typography>
          </Box>
        )}
      </Box>

      {/* View mode toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color={viewMode === 'revenue' ? 'primary' : 'text.secondary'}>
          Revenue
        </Typography>
        <Switch
          checked={viewMode === 'units'}
          onChange={e => setViewMode(e.target.checked ? 'units' : 'revenue')}
          color="primary"
        />
        <Typography variant="body2" color={viewMode === 'units' ? 'primary' : 'text.secondary'}>
          Units
        </Typography>
      </Box>
    </Box>
  );

  const renderMainContent = () => (
    <div>
      <h3>Adjust Market Concentration</h3>
      {/* 
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          p: 2,
          bgcolor: '#f8f9fa',
          borderRadius: 1,
          border: '1px solid #e3f2fd',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="h6">
            {viewMode === 'revenue' ? 'Revenue Distribution' : 'Units Distribution'}
          </Typography>

          <Box sx={{ display: 'flex', gap: 3 }}>
            <Typography variant="body2">
              <strong>Total Revenue:</strong> ${totalRevenue.toLocaleString()}
            </Typography>
            <Typography variant="body2">
              <strong>Total Units:</strong> {totalUnits.toLocaleString()}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color={viewMode === 'revenue' ? 'primary' : 'text.secondary'}>
            Revenue
          </Typography>
          <Switch
            checked={viewMode === 'units'}
            onChange={e => setViewMode(e.target.checked ? 'units' : 'revenue')}
            color="primary"
          />
          <Typography variant="body2" color={viewMode === 'units' ? 'primary' : 'text.secondary'}>
            Units
          </Typography>
        </Box>
      </Box> */}

      {renderRevenueStatusUI()}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading concentration data...</Typography>
        </Box>
      )}

      {/* Error Display */}
      {(localError || error) && (
        <div className={segmentStyles.errorMessage}>{localError || error}</div>
      )}

      {/* Main Content - Only show when data is loaded */}
      {!isLoading && concentrations.length > 0 && (
        <div className={segmentStyles.concentrationDialogContent}>
          {concentrations.map((res, index) => (
            <div key={index} className={segmentStyles.concentrationField}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <h3>{res.category_name}</h3>
                <Chip
                  label={res.status || 'estimated'}
                  size="small"
                  color={res.status === 'override' ? 'success' : 'default'}
                  variant="filled"
                />
              </Box>

              {viewMode === 'revenue' ? (
                <div>
                  <AtomSlider
                    label="Revenue in percentage"
                    value={Number((res.concentration * 100).toFixed(2))}
                    color="primary"
                    size="small"
                    min={0}
                    max={100}
                    step={0.01}
                    onChange={(e, value) => handleSliderChange(index, e, value)}
                    showValueLabel
                    customTooltip
                    tooltipContent={value => `${value.toFixed(2)}%`}
                    showInput={true}
                    inputPostfix="%"
                  />

                  <div>
                    <AtomTextField
                      className={segmentStyles.textField}
                      variant="outlined"
                      label="Revenue"
                      value={res.revenue}
                      onChange={e => handleRevenueChange(index, e)}
                      inputProps={{
                        step: 0.01,
                        min: 0,
                        max: totalRevenue,
                      }}
                    />

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 2, display: 'inline-block' }}
                    >
                      Units: {res.units_sold.toLocaleString()}
                    </Typography>
                  </div>
                </div>
              ) : (
                <div>
                  <AtomSlider
                    label="Units sold in percentage"
                    value={Number(((res.concentration_units_sold || 0) * 100).toFixed(2))}
                    color="secondary"
                    size="small"
                    min={0}
                    max={100}
                    step={0.01}
                    onChange={(e, value) => handleUnitsSliderChange(index, e, value)}
                    showValueLabel
                    customTooltip
                    tooltipContent={value => `${value.toFixed(2)}%`}
                    showInput={true}
                    inputPostfix="%"
                  />

                  <div>
                    <AtomTextField
                      className={segmentStyles.textField}
                      variant="outlined"
                      label="Units Sold"
                      value={res.units_sold}
                      onChange={e => handleUnitsChange(index, e)}
                      inputProps={{
                        step: 1,
                        min: 0,
                        max: totalUnits,
                      }}
                    />

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 2, display: 'inline-block' }}
                    >
                      Revenue: ${res.revenue.toLocaleString()}
                    </Typography>
                  </div>
                </div>
              )}

              <div>
                <AtomButton
                  variant="text"
                  color="primary"
                  size="small"
                  onClick={() => handleNoteClick(index)}
                  style={{
                    backgroundColor: res.note ? '#e3f2fd' : 'transparent',
                    fontWeight: res.note ? 'bold' : 'normal',
                  }}
                >
                  Note {res.note && '●'}
                </AtomButton>

                <AtomButton
                  variant="text"
                  color="primary"
                  size="small"
                  onClick={() => handleVersionsClick(index)}
                >
                  Versions
                </AtomButton>

                <AtomButton
                  variant="text"
                  color="primary"
                  size="small"
                  onClick={() => handleStatusClick(index)}
                  style={{
                    backgroundColor:
                      res.status && res.status !== 'estimated' ? '#e8f5e8' : 'transparent',
                    fontWeight: res.status && res.status !== 'estimated' ? 'bold' : 'normal',
                  }}
                >
                  Status: {res.status || 'estimated'}
                </AtomButton>
              </div>

              {res.note && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  style={{
                    marginTop: '8px',
                    padding: '8px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px',
                    display: 'block',
                  }}
                >
                  <strong>Note:</strong> {res.note}
                </Typography>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && concentrations.length === 0 && !error && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary">
            No concentration data available for this company segment.
          </Typography>
        </Box>
      )}

      {/* Action Buttons */}
      <AtomButton
        variant="contained"
        color="primary"
        size="large"
        className={segmentStyles.concentrationDialogAction}
        onClick={() => submitEditConcentration()}
        disabled={
          isUpdating ||
          isLoading ||
          concentrations.length === 0 ||
          Math.abs(unallocatedRevenue) > 0.01 // ✅ NEW: Disable if allocation is incorrect
        }
        sx={{
          bgcolor: Math.abs(unallocatedRevenue) > 0.01 ? 'grey.400' : 'primary.main',
        }}
      >
        {isUpdating ? 'Saving...' : 'Save'}
      </AtomButton>
      <AtomButton
        variant="outlined"
        color="primary"
        size="large"
        className={segmentStyles.concentrationDialogAction}
        onClick={() => onClose()}
        disabled={isUpdating}
      >
        Cancel
      </AtomButton>
    </div>
  );

  return (
    <div style={{ minHeight: '400px' }}>
      {getBreadcrumb()}
      {dialogView === 'main'
        ? renderMainContent()
        : dialogView === 'note'
          ? renderNoteContent()
          : dialogView === 'status'
            ? renderStatusContent()
            : dialogView === 'versions'
              ? renderVersionsContent()
              : null}
    </div>
  );
};

export default ConcentrationDialogContent;
